/* eslint-disable no-console */

import { useCallback, useMemo } from 'react';

import {
	type ColumnPinningState,
	createColumnHelper,
	functionalUpdate,
	getCoreRowModel,
	getExpandedRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type SortingState,
	type TableOptions as TanStackTableOptions,
	type VisibilityState,
} from '@tanstack/react-table'; // Import the TanStack Table types

import {
	type CharlieTableColumn,
	type CharlieTableHook,
	type CharlieTableRow,
	type TableState as CharlieTableState,
	type TableSortDirection,
} from '../hooks/use-charlie-table';

import { type CharlieTableProps } from './charlie-table';
import { manualGroupedRowModel } from './manual-group-model';

// const warningMessage = (deprecatedField: string) =>
// 	`The ${deprecatedField} in CharlieTableHook uses a different structure and will be deprecated in favor of tanstack.\n
// Please use https://tanstack.com/table/latest/docs/introduction for new types `;
// A utility function to convert CharlieTableHook to TanStack TableOptions

export const useConvertCharlieTableToTanStack = <TableDataModel extends object>(
	options: CharlieTableProps<TableDataModel>,
): TanStackTableOptions<TableDataModel> => {
	const columnHelper = createColumnHelper<TableDataModel>();
	const stableEmptyTanArray: Array<TableDataModel> = useMemo(() => [], []);
	const stableEmptyCharlesArray: Array<CharlieTableRow<TableDataModel>> = useMemo(() => [], []);

	const mapRowsToData = useCallback((rows: CharlieTableRow<TableDataModel>[]): TableDataModel[] => {
		return rows.map((row) => ({
			...row.data,
			isExpandable: row.isExpandable,
			isDefaultExpanded: row.isDefaultExpanded,
			isDisabled: row.isDisabled,
			isHidden: row.isHidden,
			groupKey: row.groupKey,
			groupValue: row.groupValue,
			overrideRender: row.overrideRender,
			subRows: row.subRows ? mapRowsToData(row.subRows) : undefined,
		}));
	}, []);

	const mappedRowToData = useMemo(() => {
		return mapRowsToData(options.rows ?? stableEmptyCharlesArray);
	}, [mapRowsToData, options.rows, stableEmptyCharlesArray]);
	// Memoize columns
	const memoizedColumns = useMemo(() => {
		return (
			options.columnDefs ??
			options.columns?.map((oldColumn) => {
				if (isCharlieTableColumn<TableDataModel>(oldColumn)) {
					return columnHelper.accessor((row) => row, {
						id: oldColumn.key,
						enableSorting: oldColumn.isSortable,
						header: () => oldColumn.content,
						minSize: oldColumn.minWidth,
						size: oldColumn.width,
						maxSize: oldColumn.maxWidth,
						cell: (props) => oldColumn.cell.render(props.getValue()),
						meta: {
							description: oldColumn.description,
							sortDescription: oldColumn.sortDescription,
						},
					});
				}
				return oldColumn;
			})
		);
	}, [columnHelper, options.columns, options.columnDefs]);

	const data: TableDataModel[] = useMemo(() => {
		return hasCharlieParam(options, 'rows') ? mappedRowToData : options.data ?? stableEmptyTanArray;
	}, [options, mappedRowToData, stableEmptyTanArray]);

	const sortingOption = hasTanstackParam(options, 'onSortingChange')
		? { onSortingChange: options.onSortingChange }
		: hasCharlieParam(options, 'onSortChange')
			? { onSortingChange: handleSortChange(options) }
			: {};

	// Convert CharlieTableHook options to TanStack TableOptions
	// @ts-expect-error have to translate between types. will be deleted
	const tanStackOptions: TanStackTableOptions<TableDataModel> = {
		...options,
		columns: memoizedColumns ?? [], //move this to its own function for memo
		data: data, //move this to its own function for memo
		initialState: {
			...(options.initialState?.columnOrder && {
				columnOrder: options.initialState?.columnOrder,
				columnVisibility:
					options.initialState.columnVisibility ??
					memoizedColumns?.reduce((acc: VisibilityState, col) => {
						if (col.id) {
							if (options.state?.columnOrder?.includes(col.id)) {
								acc[col.id] = true; // Set all columns to visible by default
							} else {
								acc[col.id] = false;
							}
						}
						return acc;
					}, {}),
			}),
			...(options.initialState?.columnVisibility && {
				columnVisibility: options.initialState.columnVisibility,
			}),
			...(options.initialState?.sorting && {
				sorting: convertToSortingState(options.initialState?.sorting),
			}),
			...(options.initialState?.expanded && { expanded: options.initialState?.expanded }),
			...(options.initialState?.rowSelection && {
				rowSelection: options.initialState?.rowSelection,
			}),
			...(options.initialState?.columnWidths && {
				columnSizing: options.initialState?.columnWidths,
			}),
			...(options.initialState?.columnSizing && {
				columnSizing: options.initialState?.columnSizing,
			}),
			...(options.initialState?.columnPinning && {
				columnPinning: convertToColumnPinningState(options.initialState?.columnPinning),
			}),
		},
		state: {
			...(options.state?.columnOrder && {
				columnOrder: options.state?.columnOrder,
				columnVisibility:
					options.state.columnVisibility ??
					memoizedColumns?.reduce((acc: VisibilityState, col) => {
						if (col.id) {
							if (options.state?.columnOrder?.includes(col.id)) {
								acc[col.id] = true; // Set all columns to visible by default
							} else {
								acc[col.id] = false;
							}
						}
						return acc;
					}, {}),
			}),
			...(options.state?.columnVisibility && { columnVisibility: options.state.columnVisibility }),
			...(options.state?.sorting && { sorting: convertToSortingState(options.state?.sorting) }),
			...(options.state?.expanded && { expanded: options.state?.expanded }),
			...(options.state?.rowSelection && { rowSelection: options.state?.rowSelection }),
			...(options.state?.columnWidths && { columnSizing: options.state?.columnWidths }),
			...(options.state?.columnSizing && { columnSizing: options.state?.columnSizing }),
			...(options.state?.grouping && { grouping: options.state.grouping }),
			...(options.state?.columnPinning && {
				columnPinning: convertToColumnPinningState(options.state?.columnPinning),
			}),
			...(options.state?.columnVisibility && { columnVisibility: options.state.columnVisibility }),
		},
		...(options.onColumnOrderChange && { onColumnOrderChange: options.onColumnOrderChange }),
		...sortingOption,
		//these are the charlieParams if they're still be used
		...(hasCharlieParam(options, 'defaultColumnWidths') && {
			defaultColumn: {
				size: options.defaultColumnWidths?.width,
				maxSize: options.defaultColumnWidths?.maxWidth,
				minSize: options.defaultColumnWidths?.minWidth,
			},
		}),
		...(hasCharlieParam(options, 'onSelectionChange') && {
			onRowSelectionChange: options.onSelectionChange,
		}),
		...(hasCharlieParam(options, 'onColumnWidthsChange') && {
			onColumnSizingChange: options.onColumnWidthsChange,
		}),

		...(hasCharlieParam(options, 'rows') &&
			hasCharlieParam(options, 'getRowId') && {
				// @ts-expect-error deleting once charliehook is deprecated
				getRowId: (row, index) => options.getRowId!({ row: { data: row }, index }),
			}),
		...(hasCharlieParam(options, 'rows') && {
			getSubRows: (row: TableDataModel & { subRows: TableDataModel }) => row.subRows,
		}),
		...(hasCharlieParam(options, 'onExpandChange') && { onExpandedChange: options.onExpandChange }),

		...(hasCharlieParam(options, 'rows') && {
			//@ts-expect-error deleting once charliehook is deprecated
			getRowCanExpand: (row) => row.original.isExpandable === true,
		}),
		//tanstack params will overwrite any charlie params
		...(hasTanstackParam(options, 'onRowSelectionChange') && {
			onRowSelectionChange: options.onRowSelectionChange,
		}),
		...(hasTanstackParam(options, 'onColumnSizingChange') && {
			onColumnSizingChange: options.onColumnSizingChange,
		}),
		...(hasTanstackParam(options, 'getSubRows') &&
			!hasCharlieParam(options, 'rows') && { getSubRows: options.getSubRows }),
		...(hasTanstackParam(options, 'defaultColumn') && { defaultColumn: options.defaultColumn }),
		...(hasTanstackParam(options, 'onExpandedChange') && {
			onExpandedChange: options.onExpandedChange,
		}),
		...(!hasCharlieParam(options, 'rows') &&
			hasTanstackParam(options, 'getRowId') && { getRowId: options.getRowId }),
		...(!hasCharlieParam(options, 'rows') &&
			hasTanstackParam(options, 'getSubRows') && { getSubRows: options.getSubRows }),
		...(hasTanstackParam(options, 'getRowCanExpand') && {
			getRowCanExpand: options.getRowCanExpand,
		}),
		enableSortingRemoval: options.enableSortingRemoval ?? false,
		enableRowSelection: options.enableRowSelection,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getGroupedRowModel: options.enableServerSideGrouping
			? manualGroupedRowModel()
			: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
	};

	return tanStackOptions;
};

function isCharlieTableColumn<TableDataModel>(
	column: any,
): column is CharlieTableColumn<TableDataModel> {
	return (
		column &&
		column.key != null &&
		column.content != null &&
		column.cell &&
		typeof column.cell.render === 'function'
	);
}

const convertToColumnPinningState = (
	pinning?: ColumnPinningState | { start?: string[]; end?: string[] },
): ColumnPinningState => {
	if (!pinning) {
		return { left: [], right: [] };
	}
	if ('start' in pinning || 'end' in pinning) {
		return {
			left: pinning.start ?? [],
			right: pinning.end ?? [],
		};
	}
	return pinning as ColumnPinningState;
};

const convertToSortingState = (
	sorting?: SortingState | { field: string; direction: TableSortDirection },
): SortingState => {
	if (Array.isArray(sorting)) {
		return sorting;
	} else if (sorting?.field) {
		return [
			{
				id: sorting.field,
				desc: sorting.direction === 'desc',
			},
		];
	}
	// If sorting is undefined, return an empty array.
	return [];
};

function hasCharlieParam<T extends object>(
	options: CharlieTableProps<T>,
	parameter: keyof CharlieTableHook<T>,
) {
	const isUsingCharles = parameter in options;
	return isUsingCharles;
}

function hasTanstackParam<T extends object>(
	options: Partial<CharlieTableProps<T>>,
	parameter: keyof TanStackTableOptions<T>,
) {
	return parameter in options;
}

const handleSortChange = <T extends object>(
	options: CharlieTableProps<T>,
): OnChangeFn<SortingState> => {
	return (sortUpdator) => {
		if (hasCharlieParam(options, 'onSortChange') && options.onSortChange) {
			// Convert Tanstack sorting to original format
			const sortingValue = functionalUpdate(
				sortUpdator,
				convertToSortingState(options.state?.sorting),
			);
			const convertedSort: CharlieTableState['sorting'] =
				sortingValue && sortingValue.length > 0
					? {
							field: sortingValue[0].id,
							direction: sortingValue[0].desc ? 'desc' : 'asc',
						}
					: undefined;
			options.onSortChange(convertedSort);
		}
		return undefined;
	};
};
